import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, CheckCircle, ArrowLeft, Clock,
  Shield, Lock, FileText, ExternalLink, Key
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { getIntegrationById } from '../../data/integrations';
import { getLogoByIntegrationId } from '../../constants/logos';

export const IntegrationDetail = () => {
  const { id } = useParams();
  const integration = getIntegrationById(id);
  const logo = integration ? getLogoByIntegrationId(integration.id) : null;

  if (!integration) {
    return <Navigate to="/integrations" replace />;
  }

  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Breadcrumb */}
        <section className="py-4 px-6 bg-secondary/30 border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/integrations" className="hover:text-foreground">Integrations</Link>
              <ChevronRight className="w-4 h-4" />
              <span>{integration.name}</span>
            </div>
          </div>
        </section>

        {/* Hero */}
        <section className="py-12 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                {logo ? (
                  <img src={logo.src} alt={logo.alt} className="w-10 h-10 object-contain" />
                ) : (
                  <span className="text-2xl font-bold text-primary">
                    {integration.name.charAt(0)}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-semibold text-foreground">{integration.name}</h1>
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${integration.status === 'available' ? 'bg-green-100 text-green-700' :
                    integration.status === 'beta' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                    {integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">{integration.description}</p>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Category:</span>
                    <span className="text-foreground font-medium">{integration.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Setup time:</span>
                    <span className="text-foreground font-medium">{integration.setupTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-12 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Read Capabilities */}
              <div className="bg-secondary/30 border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Read Capabilities</h2>
                    <p className="text-sm text-muted-foreground">Available by default</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {integration.capabilities.read.map((cap, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-foreground">{cap}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Write Capabilities */}
              <div className="bg-secondary/30 border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Write Capabilities</h2>
                    <p className="text-sm text-muted-foreground">Requires policy enablement</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {integration.capabilities.write.map((cap, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-foreground">{cap}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Authentication */}
        <section className="py-12 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-semibold text-foreground mb-6">Authentication</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Key className="w-5 h-5 text-primary" />
                  <h3 className="text-base font-semibold text-foreground">Supported Methods</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {integration.authMethods.map((method, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 bg-secondary text-sm text-foreground rounded-md"
                    >
                      {method}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-5 h-5 text-primary" />
                  <h3 className="text-base font-semibold text-foreground">Security Model</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-foreground">Credentials encrypted at rest</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-foreground">Token rotation supported</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-foreground">Least privilege scopes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Setup Guide Preview */}
        <section className="py-12 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-semibold text-foreground mb-6">Setup Guide</h2>
            <div className="bg-secondary/30 border border-border rounded-lg p-6">
              <ol className="space-y-4">
                <li className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">1</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Create API credentials in {integration.name}</h3>
                    <p className="text-sm text-muted-foreground">Navigate to your admin console and generate API credentials with appropriate scopes.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">2</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Add connector in Control Tower</h3>
                    <p className="text-sm text-muted-foreground">Navigate to Connectors → Add Connector → Select {integration.name}.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">3</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Enter credentials and test connection</h3>
                    <p className="text-sm text-muted-foreground">Provide the API credentials and verify the connection is successful.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">4</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Configure policies for write operations</h3>
                    <p className="text-sm text-muted-foreground">If needed, enable write capabilities via Policy Studio with appropriate approval gates.</p>
                  </div>
                </li>
              </ol>
              <div className="mt-6 pt-6 border-t border-border">
                <Button asChild variant="outline" size="sm">
                  <Link to={integration.docsUrl}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Full Documentation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Ready to Connect?</h2>
            <p className="text-muted-foreground mb-8">
              See how {integration.name} integrates with your IT workflows in a live demo.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/integrations">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  All Integrations
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default IntegrationDetail;
